import { useQuery } from '@apollo/client';
import { liquidGlassRowShadowSx, liquidGlassSurfaceFillSx, setTemplateTranslations } from '@focuslee/shared-mini-app';
import { Box, Collapse, Stack } from '@mui/material';
import { Page } from 'components/Page';
import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GetTemplatesQuery } from '../client/generated/graphql';
import { getTemplatesQuery } from '../client/queries/template';
import { ListCategoryWithExternalDescription } from '../client/types';
import { CategoryListItem } from '../components/category/CategoryListItem';
import { CategoriesPageSkeleton } from '../components/category/skeleton/CategoriesPageSkeleton';
import { addUnlinkedExternalTemplateCategories } from '../helpers/externalCategory';
import { openExternalLink } from '../helpers/externalLink';
import { floatingTabbarScrollBottomPad } from '../helpers/floatingTabbarLayout';
import { useCachedCategoriesWithFallback } from '../hooks/caching/category';
import { categoryPath } from '../navigation/paths';

export const CategoriesPage: FC = () => {
    const navigate = useNavigate();
    const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
    const { categories, loading, error } = useCachedCategoriesWithFallback();
    const { data: templatesData } = useQuery<GetTemplatesQuery>(getTemplatesQuery, { fetchPolicy: 'cache-first' });
    if (templatesData) setTemplateTranslations(templatesData.templateCategories);
    const displayCategories = useMemo<ListCategoryWithExternalDescription[]>(
        () => addUnlinkedExternalTemplateCategories(categories, templatesData?.templateCategories),
        [categories, templatesData],
    );

    const childrenMap = useMemo(() => {
        return displayCategories.reduce((acc, c) => {
            const parent = c.parentId;
            if (parent) {
                if (!acc[parent]) acc[parent] = [];
                acc[parent].push(c);
            }
            return acc;
        }, {} as Record<string, ListCategoryWithExternalDescription[]>);
    }, [displayCategories]);

    const rootCategories = useMemo(() => displayCategories.filter(category => !category.parentId), [displayCategories]);

    const toggleExpand = (id: string) => setExpandedMap(prev => ({ ...prev, [id]: !prev[id] }));

    const handleCategoryClick = (category: ListCategoryWithExternalDescription) => {
        if (category.externalLink) {
            openExternalLink(category.externalLink);
            return;
        }
        navigate(categoryPath.replace(':categoryId', category.id));
    };

    return (
        <Page back={false} insetEnabled={false} edgeToEdge>
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    minWidth: 0,
                    height: '100%',
                    overflow: 'hidden',
                    bgcolor: 'var(--tg-theme-secondary-bg-color)',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        boxSizing: 'border-box',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        WebkitOverflowScrolling: 'touch',
                        padding: '16px',
                        paddingTop:
                            'calc(16px + var(--tg-viewport-content-safe-area-inset-top, 0px) + var(--tg-viewport-safe-area-inset-top, 0px))',
                        paddingBottom: floatingTabbarScrollBottomPad,
                    }}
                >
                    <Stack gap="8px" sx={{ width: 'calc(100vw - 32px)', maxWidth: 'none', alignItems: 'stretch' }}>
                        {(loading || error) && <CategoriesPageSkeleton />}
                        {!loading &&
                            !error &&
                            rootCategories.map(root => {
                                const subs = childrenMap[root.id] || [];
                                const hasSubCategories = subs.length > 0;
                                const expanded = !!expandedMap[root.id];
                                return (
                                    <Box
                                        key={root.id}
                                        sx={{ width: '100%', borderRadius: '16px', overflow: 'hidden', ...liquidGlassRowShadowSx }}
                                    >
                                        <Box sx={{ borderRadius: 'inherit', ...liquidGlassSurfaceFillSx }}>
                                            <CategoryListItem
                                                category={root}
                                                childrenMap={childrenMap}
                                                expandedMap={expandedMap}
                                                onCategoryClick={() => handleCategoryClick(root)}
                                                onToggleExpand={toggleExpand}
                                            />
                                            {hasSubCategories && (
                                                <Collapse in={expanded} timeout="auto" unmountOnExit>
                                                    {subs.map(sub => (
                                                        <CategoryListItem
                                                            key={sub.id}
                                                            category={sub}
                                                            childrenMap={childrenMap}
                                                            expandedMap={expandedMap}
                                                            isSubCategory
                                                            showDivider
                                                            onCategoryClick={() => handleCategoryClick(sub)}
                                                            onToggleExpand={toggleExpand}
                                                        />
                                                    ))}
                                                </Collapse>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                    </Stack>
                </Box>
            </Box>
        </Page>
    );
};
